import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useMemo, useState } from "react";

import {
  PreviewResponse,
  useOrderMarkets,
  useOrders,
  useTimezone,
  useWorkspaceConnections,
} from "@web-app/api";
import { Avatar, Button, Icon, Input, LoadingIcon } from "@web-app/components";
import { useBrowserCache } from "@web-app/hooks";
import { INITIAL_APP_FILTER_DATA } from "@web-app/pages/common/Orders/data";
import {
  ExternalProviderKind,
  ItemCandidate,
  OptimizationItem,
  Order,
} from "@web-app/types";

import {
  externalProviderDisplay,
  formatCurrency,
  formatDuration,
  formatOptimizationDateTime,
  metersToMiles,
} from "../utils";

type Provider = { avatarUrl: string; name: string };

const candidateRef = (c: ItemCandidate): string =>
  c.providerKind ? `external:${c.providerKind}` : c.dspWorkspaceId;

const isCandidateBlocked = (c: ItemCandidate): boolean =>
  (c.missingFields ?? []).length > 0 || !!c.unavailableReason;

const itemAssignmentRef = (item: OptimizationItem): string | null => {
  if (item.assignedProviderKind) return `external:${item.assignedProviderKind}`;
  return item.assignedDspId ?? null;
};

const displayForRef = (
  ref: string,
  providerById: Map<string, Provider>,
): Provider => {
  if (ref.startsWith("external:")) {
    const kind = ref.slice("external:".length) as ExternalProviderKind;
    return externalProviderDisplay(kind);
  }
  return providerById.get(ref) ?? { avatarUrl: "", name: "Provider" };
};

type SortDir = "asc" | "desc";
type SortKey = "avg" | "cost" | "distance" | "duration" | "orders";

const sortValue = (item: OptimizationItem, key: SortKey): number => {
  switch (key) {
    case "avg":
      return item.orderCount > 0 ? item.cost / item.orderCount : 0;
    case "cost":
      return item.cost;
    case "distance":
      return item.distanceMeters;
    case "duration":
      return item.durationSeconds;
    case "orders":
      return item.orderCount;
  }
};

type ResultsStepProps = {
  isLoading: boolean;
  onBulkDelete: (itemIds: string[]) => void;
  onBulkMove: (itemIds: string[], ref: string) => void;
  onOverride: (itemId: string, ref: string) => void;
  preview?: PreviewResponse;
};

function ProviderPicker({
  item,
  onOverride,
  providerById,
}: {
  item: OptimizationItem;
  onOverride: (itemId: string, ref: string) => void;
  providerById: Map<string, Provider>;
}) {
  const [search, setSearch] = useState("");

  const assignedRef = itemAssignmentRef(item);
  if (!assignedRef) {
    return <span className="text-sm text-amber-600">Unassigned</span>;
  }

  const current = displayForRef(assignedRef, providerById);
  const available = item.candidates.filter((candidate) => {
    if (isCandidateBlocked(candidate)) return false;
    const ref = candidateRef(candidate);
    if (ref === assignedRef) return false;
    return displayForRef(ref, providerById)
      .name.toLowerCase()
      .includes(search.toLowerCase());
  });

  return (
    <Popover className="relative">
      <PopoverButton className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-gray-50">
        <Avatar name={current.name} image={current.avatarUrl} size={6} />
        <span className="text-sm text-gray-900">{current.name}</span>
        <Icon name="chevronDown" className="h-3 w-3 text-gray-400" />
      </PopoverButton>
      <PopoverPanel className="absolute z-50 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
        {({ close }) => (
          <div className="space-y-2">
            <Input
              type="search"
              value={search}
              onChange={setSearch}
              placeholder="Search provider"
            />
            <div>
              <p className="px-1 py-1 text-xs font-medium text-gray-500">
                Current Provider
              </p>
              <div className="flex items-center justify-between rounded-md px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <Avatar
                    name={current.name}
                    image={current.avatarUrl}
                    size={6}
                  />
                  <span className="text-sm text-gray-900">{current.name}</span>
                </div>
                <span className="text-sm text-gray-700">
                  {formatCurrency(item.cost, item.currency || "USD")}
                </span>
              </div>
            </div>
            {available.length > 0 && (
              <div>
                <p className="px-1 py-1 text-xs font-medium text-gray-500">
                  Available Providers
                </p>
                {available.map((candidate) => {
                  const ref = candidateRef(candidate);
                  const provider = displayForRef(ref, providerById);
                  return (
                    <button
                      key={ref}
                      type="button"
                      onClick={() => {
                        onOverride(item.itemId, ref);
                        close();
                      }}
                      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <Avatar
                          name={provider.name}
                          image={provider.avatarUrl}
                          size={6}
                        />
                        <span className="text-sm text-gray-900">
                          {provider.name}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">
                        {formatCurrency(
                          candidate.cost,
                          candidate.currency || "USD",
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}

function BulkActionBar({
  moveTargets,
  onDelete,
  onMove,
  providerById,
  selectedCount,
  onClear,
}: {
  moveTargets: string[];
  onClear: () => void;
  onDelete: () => void;
  onMove: (ref: string) => void;
  providerById: Map<string, Provider>;
  selectedCount: number;
}) {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-20 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-gray-200 bg-white px-3 py-2 text-gray-900 shadow-lg">
      <button
        type="button"
        onClick={onClear}
        className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
      >
        {selectedCount} order{selectedCount > 1 ? "s" : ""} selected
        <Icon name="xMark" className="h-3 w-3" />
      </button>
      <Popover className="relative">
        <PopoverButton className="flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50">
          <Icon name="route" className="h-3 w-3" />
          Move To
        </PopoverButton>
        <PopoverPanel className="absolute bottom-full left-0 z-50 mb-1 w-56 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
          {({ close }) =>
            moveTargets.length === 0 ? (
              <p className="px-2 py-2 text-xs text-gray-500">
                No shared providers for the selected items.
              </p>
            ) : (
              <>
                {moveTargets.map((ref) => {
                  const provider = displayForRef(ref, providerById);
                  return (
                    <button
                      key={ref}
                      type="button"
                      onClick={() => {
                        onMove(ref);
                        close();
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-50"
                    >
                      <Avatar
                        name={provider.name}
                        image={provider.avatarUrl}
                        size={6}
                      />
                      {provider.name}
                    </button>
                  );
                })}
              </>
            )
          }
        </PopoverPanel>
      </Popover>
      <Button color="light" icon="trash" onClick={onDelete}>
        Delete
      </Button>
    </div>
  );
}

function SortableHeader({
  label,
  onSort,
  sort,
  sortKey,
}: {
  label: string;
  onSort: (key: SortKey) => void;
  sort: { dir: SortDir; key: SortKey } | null;
  sortKey: SortKey;
}) {
  const active = sort?.key === sortKey;
  return (
    <th className="px-4 py-2 font-medium">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="flex items-center gap-1 hover:text-gray-700"
      >
        {label}
        <Icon
          name="chevronDown"
          className={clsx(
            "h-3 w-3",
            active ? "text-gray-700" : "text-gray-300",
            active && sort?.dir === "asc" && "rotate-180",
          )}
        />
      </button>
    </th>
  );
}

export default function ResultsStep({
  isLoading,
  onBulkDelete,
  onBulkMove,
  onOverride,
  preview,
}: ResultsStepProps) {
  const { data: connections } = useWorkspaceConnections();
  const [cpdFilter] = useBrowserCache("cpdAppFilter", INITIAL_APP_FILTER_DATA);
  const { data: marketsMap } = useOrderMarkets({
    countryCode: cpdFilter.countryCode,
  });
  const ordersFilter = useMemo(() => {
    if (cpdFilter.stateIds && cpdFilter.stateIds.length > 0) return cpdFilter;
    const allStateIds = Array.from(marketsMap?.values() || []).map(
      (state) => state.id,
    );
    return { ...cpdFilter, stateIds: [...allStateIds, "null"] };
  }, [cpdFilter, marketsMap]);
  const { data: ordersData } = useOrders(ordersFilter);
  const { getTimezone } = useTimezone();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ dir: SortDir; key: SortKey } | null>(null);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev?.key === key
        ? { dir: prev.dir === "asc" ? "desc" : "asc", key }
        : { dir: "asc", key },
    );

  const providerById = useMemo(() => {
    const map = new Map<string, Provider>();
    (connections || []).forEach((connection) => {
      map.set(connection.connectedWorkspaceId, {
        avatarUrl: connection.connectedWorkspace.avatarUrl || "",
        name: connection.connectedWorkspace.name,
      });
    });
    return map;
  }, [connections]);

  const displayIdByItemId = useMemo(() => {
    const map = new Map<string, string>();
    Array.from(ordersData?.orders.values() || []).forEach((order) => {
      map.set(order.id, order.externalId || order.displayId);
      if (order.route) {
        map.set(
          order.route.id,
          order.route.externalId || order.route.displayId,
        );
      }
    });
    return map;
  }, [ordersData]);

  const timezoneByItemId = useMemo(() => {
    const map = new Map<string, Order["timezone"]>();
    Array.from(ordersData?.orders.values() || []).forEach((order) => {
      map.set(order.id, order.timezone);
      if (order.route && !map.has(order.route.id)) {
        map.set(order.route.id, order.timezone);
      }
    });
    return map;
  }, [ordersData]);

  const itemTime = (itemId: string, value?: Date | string | null) =>
    formatOptimizationDateTime(
      value,
      getTimezone(timezoneByItemId.get(itemId)),
    );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!preview) {
    return (
      <p className="py-12 text-center text-sm text-gray-500">
        No results to show.
      </p>
    );
  }

  const toggleSelected = (itemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleExpanded = (itemId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectedIds = Array.from(selected);
  const selectedItemsList = preview.items.filter((item) =>
    selected.has(item.itemId),
  );
  const moveTargets =
    selectedItemsList.length === 0
      ? []
      : selectedItemsList
          .map((item) =>
            item.candidates
              .filter((c) => !isCandidateBlocked(c))
              .map(candidateRef),
          )
          .reduce((shared, refs) => shared.filter((ref) => refs.includes(ref)));

  const sortedItems = sort
    ? [...preview.items].sort((a, b) => {
        const diff = sortValue(a, sort.key) - sortValue(b, sort.key);
        return sort.dir === "asc" ? diff : -diff;
      })
    : preview.items;

  return (
    <div className="space-y-4">
      <div className="overflow-visible rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="w-10 px-4 py-2" />
              <th className="px-4 py-2 font-medium">Name / Id</th>
              <SortableHeader
                label="Distance"
                onSort={toggleSort}
                sort={sort}
                sortKey="distance"
              />
              <SortableHeader
                label="Total time"
                onSort={toggleSort}
                sort={sort}
                sortKey="duration"
              />
              <th className="px-4 py-2 font-medium">Pickup time</th>
              <SortableHeader
                label="Total Drops"
                onSort={toggleSort}
                sort={sort}
                sortKey="orders"
              />
              <th className="px-4 py-2 font-medium">Provider</th>
              <SortableHeader
                label="Total cost"
                onSort={toggleSort}
                sort={sort}
                sortKey="cost"
              />
              <SortableHeader
                label="Avg cost per delivery"
                onSort={toggleSort}
                sort={sort}
                sortKey="avg"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedItems.map((item) => {
              const isRoute = item.kind === "ROUTE";
              const isExpanded = expanded.has(item.itemId);
              const children = isRoute ? item.children : [];
              const assignedRef = itemAssignmentRef(item);
              const childProvider = assignedRef
                ? displayForRef(assignedRef, providerById)
                : undefined;
              return (
                <Fragment key={`${item.kind}:${item.itemId}`}>
                  <tr>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        {isRoute && children.length > 0 && (
                          <button
                            type="button"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            onClick={() => toggleExpanded(item.itemId)}
                          >
                            <Icon
                              name={isExpanded ? "chevronDown" : "chevronRight"}
                              className="h-3 w-3 text-gray-500"
                            />
                          </button>
                        )}
                        <input
                          type="checkbox"
                          checked={selected.has(item.itemId)}
                          onChange={() => toggleSelected(item.itemId)}
                          aria-label={`Select ${item.itemId}`}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-900">
                      {displayIdByItemId.get(item.itemId) || item.itemId}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {metersToMiles(item.distanceMeters)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {formatDuration(item.durationSeconds)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {itemTime(item.itemId, item.pickupTime)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {item.orderCount}
                    </td>
                    <td className="px-4 py-2">
                      <ProviderPicker
                        item={item}
                        onOverride={onOverride}
                        providerById={providerById}
                      />
                    </td>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {assignedRef ? (
                        formatCurrency(item.cost, item.currency || "USD")
                      ) : item.candidates.some(
                          (c) => (c.missingFields ?? []).length > 0,
                        ) ? (
                        <span className="text-amber-600 text-xs font-normal">
                          Missing details
                        </span>
                      ) : item.candidates.some((c) => !!c.unavailableReason) ? (
                        <span
                          className="text-amber-600 text-xs font-normal"
                          title={
                            item.candidates.find((c) => c.unavailableReason)
                              ?.unavailableReason ?? ""
                          }
                        >
                          Not available
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {item.orderCount > 0
                        ? formatCurrency(
                            item.cost / item.orderCount,
                            item.currency || "USD",
                          )
                        : "—"}
                    </td>
                  </tr>
                  {isExpanded &&
                    children.map((child) => (
                      <tr key={child.orderId} className="bg-gray-50/50">
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2 pl-8 text-gray-500">
                          {displayIdByItemId.get(child.orderId) ||
                            child.orderId}
                        </td>
                        <td className="px-4 py-2 text-gray-500">—</td>
                        <td className="px-4 py-2 text-gray-500">—</td>
                        <td className="px-4 py-2 text-gray-500">
                          {itemTime(item.itemId, child.pickupTime)}
                        </td>
                        <td className="px-4 py-2 text-gray-500">—</td>
                        <td className="px-4 py-2">
                          {childProvider ? (
                            <div className="flex items-center gap-2">
                              <Avatar
                                name={childProvider.name}
                                image={childProvider.avatarUrl || ""}
                                size={6}
                              />
                              <span className="text-sm text-gray-500">
                                {childProvider.name}
                              </span>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">—</td>
                        <td className="px-4 py-2 text-gray-500">—</td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <BulkActionBar
        selectedCount={selected.size}
        moveTargets={moveTargets}
        providerById={providerById}
        onClear={() => setSelected(new Set())}
        onMove={(dspId) => {
          onBulkMove(selectedIds, dspId);
          setSelected(new Set());
        }}
        onDelete={() => {
          onBulkDelete(selectedIds);
          setSelected(new Set());
        }}
      />
    </div>
  );
}
